import * as React from 'react'
import dynamic from 'next/dynamic'

import { Button, PlatformMessage } from '@thg-commerce/gravity-elements'
import { ButtonProps } from '@thg-commerce/gravity-elements/Button/Button'
import {
  Margin,
  margin,
  mq,
  spacing,
  styled,
  Text,
  TextStyling,
} from '@thg-commerce/gravity-theme'

import { useTheme } from '../../theme'

const IconSuccess = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/IconSuccess'),
  { ssr: true, loading: () => <div style={{ width: 24, height: 24 }} /> },
)
const IconInfo = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/IconInfo'),
  { ssr: true, loading: () => <div style={{ width: 24, height: 24 }} /> },
)

export const StyledIconSuccess = styled(IconSuccess)`
  margin-right: ${spacing(2)};
  path {
    fill: ${(props) => props.theme.colors.success.base};
  }
`

export const QualifiedContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(2)};
`

export const StyledPlatformMessage = styled(PlatformMessage)`
  margin-bottom: ${spacing(2)};
`

export const QualifiedText = styled.p`
  ${Text('bodyText', 'alternate')}
  color: ${(props) => props.theme.colors.success.base};
`

export const SelectedGifts = styled.p<{
  bold?: boolean
  enableGiftBasketLayout?: boolean
  textStyling?: TextStyling
  margin?: Margin
}>`
  margin-bottom: ${spacing(2)};
  ${(props) => Text('bodyText', props.bold ? 'alternate' : 'default')}

  ${(props) =>
    props.enableGiftBasketLayout && props.margin && margin(props.margin)}

  ${(props) =>
    props.enableGiftBasketLayout &&
    props.textStyling?.entry &&
    props.textStyling?.style &&
    Text(props.textStyling.entry, props.textStyling.style)}

  ${(props) =>
    props.enableGiftBasketLayout &&
    props.textStyling?.textColor &&
    `color: ${props.textStyling.textColor};`}
`

export const TierContainer = styled.div<{
  enableGiftBasketLayout?: boolean
  margin?: Margin
}>`
  margin-top: ${spacing(3)};

  &:not(:last-child) {
    margin-bottom: ${spacing(4)};
  }

  ${(props) =>
    props.enableGiftBasketLayout && props.margin && margin(props.margin)}
`

export const ProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing(2)};
`

export const Product = styled.div<{
  isSelected: boolean
  disabled: boolean
  enableGiftBasketLayout?: boolean
}>`
  display: flex;
  flex-direction: row;
  align-items: ${(props) =>
    props.enableGiftBasketLayout ? 'stretch' : 'center'};
  padding: ${(props) => (props.enableGiftBasketLayout ? '0' : spacing(1))};
  background-color: ${(props) =>
    props.isSelected && props.enableGiftBasketLayout
      ? props.theme.colors.success.light
      : props.theme.colors.palette.greys.white};
  ${(props) =>
    props.enableGiftBasketLayout
      ? props.isSelected
        ? `border: 1px solid ${props.theme.colors.success.base};`
        : 'border: none'
      : props.isSelected
      ? `box-shadow: 0px 0px 0px 2px ${props.theme.colors.palette.brand.base} inset;`
      : 'box-shadow: none;'}

  ${(props) =>
    props.disabled
      ? `
        opacity: 0.5;
        cursor: default;
      `
      : 'cursor: pointer;'}
`

export const ProductContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  margin: 4px ${spacing(1)};
  align-items: flex-start;
  gap: 4px;
  cursor: pointer;
`

export const ProductTitle = styled.p<{
  enableGiftBasketLayout?: boolean
  textStyling?: TextStyling
  margin?: Margin
}>`
  margin: auto 0 auto ${spacing(2)};
  flex: 1;
  text-align: left;

  ${(props) =>
    props.enableGiftBasketLayout && props.margin && margin(props.margin)}

  ${(props) =>
    props.enableGiftBasketLayout &&
    props.textStyling?.entry &&
    props.textStyling?.style &&
    Text(props.textStyling.entry, props.textStyling.style)}

  ${(props) =>
    props.enableGiftBasketLayout &&
    props.textStyling?.textColor &&
    `color: ${props.textStyling.textColor};`}

  ${(props) =>
    props.enableGiftBasketLayout &&
    `
    margin: 0;
    flex: none;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`

export const ProductSubtitle = styled.p<{
  enableGiftBasketLayout?: boolean
  textStyling?: TextStyling
  margin?: Margin
}>`
  margin: 0;
  font-size: 15px;
  line-height: 20px;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    font-size: 16px;
    line-height: 22px;
  }

  ${(props) =>
    props.enableGiftBasketLayout &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
  `}

  ${(props) =>
    props.enableGiftBasketLayout && props.margin && margin(props.margin)}

  ${(props) =>
    props.enableGiftBasketLayout &&
    props.textStyling?.entry &&
    props.textStyling?.style &&
    Text(props.textStyling.entry, props.textStyling.style)}

  ${(props) =>
    props.enableGiftBasketLayout &&
    props.textStyling?.textColor &&
    `color: ${props.textStyling.textColor};`}
`

export const SeeMoreDetails = styled.button<{
  enableGiftBasketLayout?: boolean
  textStyling?: TextStyling
}>`
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
  font-size: 15px;
  line-height: 20px;
  margin-top: auto;

  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    font-size: 16px;
    line-height: 22px;
  }

  ${(props) =>
    props.enableGiftBasketLayout &&
    props.textStyling?.entry &&
    props.textStyling?.style &&
    Text(props.textStyling.entry, props.textStyling.style)}

  ${(props) =>
    props.enableGiftBasketLayout &&
    props.textStyling?.textDecoration &&
    `text-decoration: ${props.textStyling.textDecoration};`}

  ${(props) =>
    props.enableGiftBasketLayout &&
    props.textStyling?.textColor &&
    `color: ${props.textStyling.textColor};`}
`

export const InfoIconButton = styled(Button)`
  background: none;
  align-self: center;
  margin-left: ${spacing(1)};
  width: 32px;
`

export const InfoIcon = (props: ButtonProps) => {
  const theme = useTheme()
  return (
    <InfoIconButton emphasis="low" {...props}>
      <IconInfo
        fill={
          theme.pageTheme.selectYourSample.productDescription.accordion
            .chevronIcon?.fillColor
        }
      />
    </InfoIconButton>
  )
}
